import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Lock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Lien invalide — jeton manquant"); return; }
    if (pw.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 border-slate-200 shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#0066FF] flex items-center justify-center text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-2xl tracking-tight text-slate-900">FIO<span className="text-[#0066FF]">TELECOM</span></span>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Mot de passe modifié</h2>
            <p className="text-sm text-slate-600 mt-3">Redirection vers la page de connexion…</p>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Nouveau mot de passe</h2>
            <p className="text-sm text-slate-500 mt-2">
              Choisissez un mot de passe d'au moins <b>8 caractères</b>. Vous pourrez vous connecter immédiatement après validation.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-form">
              <div>
                <Label>Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="reset-pw-input"
                    type="password" required minLength={8} autoComplete="new-password"
                    value={pw} onChange={(e) => setPw(e.target.value)}
                    className="pl-9 h-11" placeholder="••••••••"
                  />
                </div>
              </div>
              <div>
                <Label>Confirmez le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="reset-pw2-input"
                    type="password" required minLength={8} autoComplete="new-password"
                    value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="pl-9 h-11" placeholder="••••••••"
                  />
                </div>
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading || !token} className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC]">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement…</> : "Définir le mot de passe"}
              </Button>
              <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-slate-800 mt-2">
                Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
